import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Search, Filter, Download, Phone, Mail, MessageSquare, Eye, Users, TrendingUp, CheckCircle2, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { useEffect } from "react";
import API from "../api/api01";
import { toast } from "sonner";
import { exportToCSV } from "../utils/exportUtils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  alternatePhone?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  course: string;
  stage: string;
  source: "Website" | "Walk-in" | "Campaign" | "Referral";
  priority: "High" | "Medium" | "Low";
  counselor: {
    name: string;
    avatar: string;
  };
  appliedDate: string;
  lastFollowUp: string;
  status: "Active" | "Lost" | "Converted";
}

export function LeadAll() {
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [messageText, setMessageText] = useState("");

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await API.get("/leads/");
      if (response.data) {
        const allLeads = response.data.flatMap((s: any) => 
          s.leads.map((l: any) => ({
            id: l.id,
            name: l.name,
            email: l.email,
            phone: l.phone || "N/A",
            alternatePhone: l.alternate_phone || "",
            address: l.address || "",
            city: l.city || "",
            state: l.state || "",
            pincode: l.pincode || "",
            course: l.course || "N/A",
            stage: s.name,
            source: l.source || "Website",
            priority: l.priority || "Medium",
            counselor: {
              name: l.counselor_name || "Unassigned",
              avatar: l.counselor_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${l.name}`,
            },
            appliedDate: l.applied_date ? new Date(l.applied_date).toLocaleDateString() : "Today",
            lastFollowUp: l.last_follow_up || "New",
            status: l.lead_status === "enrolled" ? "Converted" : (l.lead_status === "not-interested" ? "Lost" : "Active"),
          }))
        );
        setLeads(allLeads);
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High": return "bg-red-500/10 text-red-500";
      case "Medium": return "bg-orange-500/10 text-orange-500";
      case "Low": return "bg-green-500/10 text-green-500";
      default: return "";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active": return "bg-blue-500/10 text-blue-500";
      case "Lost": return "bg-red-500/10 text-red-500";
      case "Converted": return "bg-secondary/10 text-secondary";
      default: return "";
    }
  };

  const calculateStats = () => {
    const totalLeads = leads.length;
    const activeLeads = leads.filter(lead => lead.status === "Active").length;
    const convertedLeads = leads.filter(lead => lead.status === "Converted").length;
    const conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100).toFixed(0) : 0;
    return { totalLeads, activeLeads, convertedLeads, conversionRate };
  };

  const stats = calculateStats();

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch = lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone.includes(searchQuery) ||
      lead.course.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStage = stageFilter === "all" || lead.stage === stageFilter;
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
    return matchesSearch && matchesStage && matchesStatus;
  });

  const handleCall = () => {
    if (selectedLead?.phone) {
      toast.success(`Calling ${selectedLead?.name} at ${selectedLead?.phone}`);
      setIsCallDialogOpen(false);
    }
  };

  const handleSendMessage = () => {
    toast.success(`Message sent to ${selectedLead?.name}!`);
    setMessageText("");
    setIsMessageDialogOpen(false);
  };

  const handleUpdateStage = async (newStage: string) => {
    if (!selectedLead) return;
    try {
      await API.patch(`/leads/${selectedLead.id}`, { lead_status: newStage });
      toast.success(`Lead moved to ${newStage}`);
      fetchLeads();
      // Update selected lead to reflect changes in dialog
      setSelectedLead(prev => prev ? { ...prev, stage: newStage, status: newStage === "enrolled" ? "Converted" : (newStage === "not-interested" || newStage === "Lost" ? "Lost" : "Active") } : null);
    } catch (err) {
      toast.error("Failed to update lead status");
    }
  };

  const handleExport = () => {
    const success = exportToCSV(leads, "all_leads_export.csv", {
      name: "Lead Name",
      email: "Email",
      phone: "Phone Number",
      course: "Applied Course",
      stage: "Current Stage",
      source: "Lead Source",
      priority: "Priority",
      appliedDate: "Applied On",
      status: "Lead Status",
    });

    if (success) {
      toast.success("Lead data exported to CSV successfully!");
    } else {
      toast.error("Failed to export lead data.");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 bg-slate-50 dark:bg-[#020617] min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1>All Leads</h1>
          <p className="text-muted-foreground mt-1">
            Complete overview of all leads in the system
          </p>
        </div>
        <Button 
          variant="outline" 
          className="rounded-xl border-slate-200 hover:bg-slate-100 dark:border-slate-800 dark:hover:bg-slate-800 transition-all shadow-sm"
          onClick={handleExport}
        >
          <Download className="h-4 w-4 mr-2 text-primary" />
          Export
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-primary/10">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalLeads}</p>
                <p className="text-sm text-muted-foreground">Total Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-500/10">
                <Clock className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeLeads}</p>
                <p className="text-sm text-muted-foreground">Active Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-secondary/10">
                <CheckCircle2 className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.convertedLeads}</p>
                <p className="text-sm text-muted-foreground">Converted</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-chart-3/10">
                <TrendingUp className="h-6 w-6 text-chart-3" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.conversionRate}%</p>
                <p className="text-sm text-muted-foreground">Conversion Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500" />
              <Input
                placeholder="Search leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full rounded-lg bg-slate-50 dark:bg-[#020617] border border-slate-200 dark:border-slate-800"
              />
            </div>

            <Select value={stageFilter} onValueChange={setStageFilter}>
  <SelectTrigger className="w-full md:w-48 rounded-lg border border-border bg-white dark:bg-[#0f172a] shadow-sm">
    <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
    <SelectValue placeholder="Stage" />
  </SelectTrigger>

  <SelectContent className="bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-slate-800 shadow-xl rounded-lg p-1">
    <SelectItem className="rounded-md hover:bg-muted" value="all">
      All Stages
    </SelectItem>
    <SelectItem className="rounded-md hover:bg-muted" value="New Enquiries">
      New Enquiries
    </SelectItem>
    <SelectItem className="rounded-md hover:bg-muted" value="Contacted">
      Contacted
    </SelectItem>
    <SelectItem className="rounded-md hover:bg-muted" value="Application Submitted">
      Application Submitted
    </SelectItem>
    <SelectItem className="rounded-md hover:bg-muted" value="Enrolled">
      Enrolled
    </SelectItem>
  </SelectContent>
</Select>

<Select value={statusFilter} onValueChange={setStatusFilter}>
  <SelectTrigger className="w-full md:w-48 rounded-lg border border-border bg-white dark:bg-[#0f172a] shadow-sm">
    <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
    <SelectValue placeholder="Status" />
  </SelectTrigger>

  <SelectContent className="bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-slate-800 shadow-xl rounded-lg p-1">
    <SelectItem className="rounded-md hover:bg-muted" value="all">
      All Status
    </SelectItem>
    <SelectItem className="rounded-md hover:bg-muted" value="Active">
      Active
    </SelectItem>
    <SelectItem className="rounded-md hover:bg-muted" value="Lost">
      Lost
    </SelectItem>
    <SelectItem className="rounded-md hover:bg-muted" value="Converted">
      Converted
    </SelectItem>
  </SelectContent>
</Select>
          </div>
        </CardContent>
      </Card>

     {/* Leads Table */}
<Card className="shadow-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0f172a]">
  <CardHeader className="pb-3">
    <CardTitle className="text-lg font-semibold tracking-tight">
      Lead Directory
    </CardTitle>
  </CardHeader>

  <CardContent>
    <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-800">

      <Table>

        {/* Sticky Header */}
        <TableHeader className="sticky top-0 bg-slate-50 dark:bg-[#0f172a] border-b border-slate-200 dark:border-slate-800 backdrop-blur z-10">
          <TableRow>
            <TableHead>Lead</TableHead>
            <TableHead>Course</TableHead>
            <TableHead>Stage</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="hidden md:table-cell">Applied</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {filteredLeads.map((lead) => (
            <TableRow
              key={lead.id}
              className="hover:bg-slate-50 dark:hover:bg-[#171717] transition-colors duration-200"
            >

              {/* Lead */}
              <TableCell>
                <div className="flex items-center gap-3">

                  <Avatar className="h-9 w-9 border">
                    <AvatarImage src={lead.counselor.avatar} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {lead.name.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>

                  <div className="leading-tight">
                    <p className="font-medium">{lead.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {lead.email}
                    </p>
                  </div>

                </div>
              </TableCell>

              {/* Course */}
              <TableCell className="text-muted-foreground">
                {lead.course}
              </TableCell>

              {/* Stage */}
              <TableCell>
                <Badge className="bg-blue-500/10 text-blue-600 border border-blue-200 dark:border-blue-900">
                  {lead.stage}
                </Badge>
              </TableCell>

              {/* Priority */}
              <TableCell>
                <Badge className={getPriorityColor(lead.priority)}>
                  {lead.priority}
                </Badge>
              </TableCell>

              {/* Status */}
              <TableCell>
                <Badge className={getStatusColor(lead.status)}>
                  {lead.status}
                </Badge>
              </TableCell>

              {/* Date */}
              <TableCell className="hidden md:table-cell text-muted-foreground">
                {lead.appliedDate}
              </TableCell>

              {/* Actions */}
              <TableCell className="text-right">
                <TooltipProvider>
                  <div className="flex justify-end gap-1">

                    {/* Call */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 hover:bg-blue-100 hover:text-blue-600 dark:hover:bg-blue-900/40 transition"
                          onClick={() => {
                            setSelectedLead(lead);
                            setIsCallDialogOpen(true);
                          }}
                        >
                          <Phone className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Call</TooltipContent>
                    </Tooltip>

                    {/* Message */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 hover:bg-green-100 hover:text-green-600 dark:hover:bg-green-900/40 transition"
                          onClick={() => {
                            setSelectedLead(lead);
                            setIsMessageDialogOpen(true);
                          }}
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Message</TooltipContent>
                    </Tooltip>

                    {/* View */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 hover:bg-purple-100 hover:text-purple-600 dark:hover:bg-purple-900/40 transition"
                          onClick={() => {
                            setSelectedLead(lead);
                            setIsViewDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>View Lead</TooltipContent>
                    </Tooltip>

                  </div>
                </TooltipProvider>
              </TableCell>

            </TableRow>
          ))}
        </TableBody>

      </Table>

    </div>
  </CardContent>
</Card>
      {/* Call Dialog */}
      <Dialog open={isCallDialogOpen} onOpenChange={setIsCallDialogOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>Call Lead</DialogTitle>
            <DialogDescription>
              Initiate a call to the lead.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6 gap-4">
            <Avatar className="h-20 w-20 border-2 border-primary/20">
              <AvatarImage src={selectedLead?.counselor.avatar} />
              <AvatarFallback className="text-2xl">{selectedLead?.name.substring(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="text-center">
              <h3 className="text-xl font-bold">{selectedLead?.name}</h3>
              <p className="text-muted-foreground">{selectedLead?.phone}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCallDialogOpen(false)}>Cancel</Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={handleCall}>
              <Phone className="h-4 w-4 mr-2" />
              Call Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>Send Message</DialogTitle>
            <DialogDescription>
              Send a quick message to {selectedLead?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Type your message here..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="min-h-[100px] bg-slate-50 dark:bg-[#020617]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMessageDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendMessage} disabled={!messageText.trim()}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Lead Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[600px] bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800 p-0 overflow-hidden">
          <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 p-6 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-white dark:border-slate-700 shadow-sm">
                <AvatarImage src={selectedLead?.counselor.avatar} />
                <AvatarFallback className="text-xl font-bold bg-primary/10 text-primary">
                  {selectedLead?.name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold tracking-tight">{selectedLead?.name}</h2>
                  {selectedLead && (
                    <Badge className={getStatusColor(selectedLead.status)}>
                      {selectedLead.status}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" />
                  <span className="text-sm">{selectedLead?.email}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Contact Details</Label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-md bg-blue-500/10 text-blue-500">
                      <Phone className="h-4 w-4" />
                    </div>
                    <span className="text-sm font-medium">{selectedLead?.phone || "N/A"}</span>
                  </div>
                  {selectedLead?.alternatePhone && (
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-md bg-slate-500/10 text-slate-500">
                        <Phone className="h-4 w-4" />
                      </div>
                      <span className="text-sm text-muted-foreground">{selectedLead.alternatePhone}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Academic Interest</Label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Course:</span>
                    <span className="font-medium">{selectedLead?.course}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Current Stage:</span>
                    <Select 
                      value={selectedLead?.stage} 
                      onValueChange={handleUpdateStage}
                    >
                      <SelectTrigger className="h-8 w-[180px] bg-blue-500/5 text-blue-600 border-blue-200">
                        <SelectValue placeholder="Select Stage" />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
                        <SelectItem value="new-enquiries">New Enquiries</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="application-submitted">Application Submitted</SelectItem>
                        <SelectItem value="admission-offered">Admission Offered</SelectItem>
                        <SelectItem value="enrolled">Enrolled</SelectItem>
                        <SelectItem value="not-interested">Not Interested</SelectItem>
                        <SelectItem value="Lost">Lost</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Lead Metadata</Label>
                <div className="mt-2 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Source:</span>
                    <span className="font-medium">{selectedLead?.source}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Priority:</span>
                    {selectedLead && (
                      <Badge className={getPriorityColor(selectedLead.priority)}>
                        {selectedLead.priority}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Applied On:</span>
                    <span className="font-medium">{selectedLead?.appliedDate}</span>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Assigned Counselor</Label>
                <div className="mt-2 flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={selectedLead?.counselor.avatar} />
                    <AvatarFallback>{selectedLead?.counselor.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{selectedLead?.counselor.name}</span>
                </div>
              </div>
            </div>
          </div>

          {(selectedLead?.address || selectedLead?.city) && (
            <div className="px-6 pb-6 pt-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Location</Label>
              <p className="mt-2 text-sm">
                {[selectedLead.address, selectedLead.city, selectedLead.state, selectedLead.pincode]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            </div>
          )}

          <div className="bg-slate-50 dark:bg-slate-900/50 p-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span>Last Follow up: {selectedLead?.lastFollowUp}</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-9 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/40"
                onClick={() => {
                  setIsViewDialogOpen(false);
                  setIsCallDialogOpen(true);
                }}
              >
                <Phone className="h-4 w-4 mr-2" />
                Call
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-9 hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-900/40"
                onClick={() => {
                  setIsViewDialogOpen(false);
                  setIsMessageDialogOpen(true);
                }}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
